'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: string;
  name: string;
  age: number;
  condition: string;
  image: string;
  alt: string;
  rating: number;
  quote: string;
  treatmentDuration: string;
}

const TestimonialsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials: Testimonial[] = [
  {
    id: "testimonial-1",
    name: "Sarah Mitchell",
    age: 34,
    condition: "Anxiety & Depression",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bdbe6c11-1763300715032.png",
    alt: "Professional woman with brown hair in blue blazer smiling confidently at camera",
    rating: 5,
    quote: "HealConnect changed my life. Having access to a therapist from home made it so much easier to stay consistent with treatment. I've been anxiety-free for 6 months now.",
    treatmentDuration: "8 months"
  },
  {
    id: "testimonial-2",
    name: "Michael Chen",
    age: 42,
    condition: "Type 2 Diabetes",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_12aa094c4-1763295366196.png",
    alt: "Asian man in casual gray sweater with friendly smile in outdoor setting",
    rating: 5,
    quote: "Managing my diabetes has never been easier. The monthly check-ins and personalized meal plans helped me lower my A1C by 2 points. Highly recommend!",
    treatmentDuration: "1 year"
  },
  {
    id: "testimonial-3",
    name: "Emily Rodriguez",
    age: 29,
    condition: "Hormonal Acne",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_130d94d39-1763296768059.png",
    alt: "Young Hispanic woman with clear skin and natural makeup smiling warmly",
    rating: 5,
    quote: "After years of struggling with acne, the dermatologist at HealConnect finally found the right treatment. My skin has cleared up completely and my confidence is back.",
    treatmentDuration: "5 months"
  },
  {
    id: "testimonial-4",
    name: "David Thompson",
    age: 51,
    condition: "Hypertension",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f40969ca-1763294911047.png",
    alt: "Middle-aged man with gray beard in navy shirt looking content and healthy",
    rating: 5,
    quote: "The convenience of telehealth combined with excellent medical care has been a game-changer. My blood pressure is now under control and I feel great.",
    treatmentDuration: "10 months"
  }];


  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Real Stories, Real Results
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Hear from patients who transformed their health with HealConnect
          </p>
        </div>

        <div className="max-w-4xl mx-auto relative">
          <div className="bg-card border border-border rounded-2xl p-8 lg:p-12 shadow-elevation-lg">
            <div className="flex flex-col lg:flex-row gap-8 items-center">
              <div className="w-32 h-32 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                <AppImage
                  src={testimonials[currentIndex].image}
                  alt={testimonials[currentIndex].alt}
                  className="w-full h-full object-cover" />

              </div>

              <div className="flex-1 text-center lg:text-left">
                <div className="flex justify-center lg:justify-start gap-1 mb-4">
                  {[...Array(testimonials[currentIndex].rating)].map((_, i) =>
                  <Icon key={i} name="StarIcon" size={20} className="text-warning" variant="solid" />
                  )}
                </div>

                <blockquote className="text-lg text-foreground mb-6 italic">
                  "{testimonials[currentIndex].quote}"
                </blockquote>

                <div className="space-y-2">
                  <p className="font-heading font-semibold text-foreground">
                    {testimonials[currentIndex].name}, {testimonials[currentIndex].age}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {testimonials[currentIndex].condition} • {testimonials[currentIndex].treatmentDuration} of treatment
                  </p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={prevTestimonial}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 w-12 h-12 bg-card border border-border rounded-full flex items-center justify-center transition-smooth hover:bg-primary hover:text-primary-foreground hover:border-primary shadow-elevation-md"
            aria-label="Previous testimonial">

            <Icon name="ChevronLeftIcon" size={24} />
          </button>

          <button
            onClick={nextTestimonial}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 w-12 h-12 bg-card border border-border rounded-full flex items-center justify-center transition-smooth hover:bg-primary hover:text-primary-foreground hover:border-primary shadow-elevation-md"
            aria-label="Next testimonial">

            <Icon name="ChevronRightIcon" size={24} />
          </button>

          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => goToTestimonial(index)}
              className={`w-2.5 h-2.5 rounded-full transition-smooth ${
              index === currentIndex ? 'bg-primary w-8' : 'bg-border hover:bg-muted-foreground'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsCarousel;